import { LicenseCard } from '@/components/performance/LicenseCard';
import { useMemo, useState } from 'react';
import { usePagination } from '@/hooks/usePagination';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, Search, ChevronRight, AlertTriangle, AlertCircle, MoreHorizontal, SlidersHorizontal, RefreshCw, X } from 'lucide-react';
import { CycleConfigPanel } from '@/components/performance/CycleConfigPanel';
import {
  useTalentMapTeam, useTalentMapAllForPeople, useTalentMapLogExport,
  useTalentMapSyncAll, useCycleConfig, useTalentMapAccess, useSetTalentLicense,
} from '@/hooks/usePerformance';
import { useAuth } from '@/contexts/SharedAuthContext';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { TeamDistributionPanel } from '@/components/performance/talent-map/TeamDistributionPanel';
import { LeaderProgressCard } from '@/components/performance/talent-map/LeaderProgressCard';
import { DTalkLicenseManagerDialog } from '@/components/performance/dtalk/DTalkLicenseManagerDialog';
import { curveBucket, CURVE_ORDER, isOnLicense } from '../../../../../shared/talent-map-stats';
import {
  CATEGORY_CONFIG, curvePointMeta,
  displayConfidencePercent, getInitials, getAvatarColor, formatPeriodLabel,
  type TalentCategory, type TalentTeamRow,
} from '@/components/performance/talent-map/talentMapShared';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { downloadCsv } from '@/lib/csv';

const CATEGORY_LABELS: Record<string, string> = {
  top_performer: 'Top Performer', performance_solida: 'Performance Sólida',
  cumple_con_oportunidades: 'Cumple con oportunidades', low_performance: 'Low Performance', onboarding: 'Onboarding',
};
const STATUS_LABELS: Record<string, string> = {
  not_started: 'Sin iniciar', draft: 'Borrador', pending_evidence: 'Pendiente evidencia',
  confirmed: 'Confirmada', hrbp_modified: 'Modificada HRBP', inactive: 'Inactivo',
  auto_assigned: 'Automática (sin evaluar)',
};

function countBy(rows: TalentTeamRow[], fn: (r: TalentTeamRow) => string | null | undefined) {
  const m = new Map<string, number>();
  for (const r of rows) { const k = fn(r); if (k) m.set(k, (m.get(k) ?? 0) + 1); }
  return m;
}

function exportTeamCSV(rows: TalentTeamRow[]) {
  downloadCsv(
    `mapa-talento-equipo-${new Date().toISOString().slice(0, 10)}.csv`,
    ['ID', 'Email', 'Nombre', 'Apellido', 'Nivel', 'Rol', 'Equipo', 'Área', 'Fecha ingreso', 'Evaluador', 'Elegibilidad', 'Categoría final', 'Estado proceso', 'Actualizado', 'Actualizado por'],
    rows.map(m => [
      m.employeeId ?? '', m.email, m.name, m.lastName, m.impactLevel, m.role ?? '', m.team ?? '', m.area ?? '',
      m.startDate ?? '', m.leaderEmail ?? '',
      m.eligibilityStatus === 'onboarding' ? 'Onboarding' : m.eligibilityStatus === 'inactive' ? 'Inactivo' : m.eligibilityStatus === 'license' ? 'Licencia' : 'Elegible',
      m.finalCategory ? CATEGORY_LABELS[m.finalCategory] : '',
      STATUS_LABELS[m.processStatus] ?? m.processStatus,
      m.updatedAt ? m.updatedAt.slice(0, 10) : '', m.updatedBy ?? '',
    ]),
  );
}

function exportOrgCSV(records: any[]) {
  downloadCsv(
    `mapa-de-talento-${new Date().toISOString().slice(0, 10)}.csv`,
    ['ID', 'Mail', 'Nombre', 'Apellido', 'Rol', 'Nivel', 'Equipo', 'Área', 'People Manager', 'Evaluado por', 'N+1 Manager', 'País', 'Fecha ingreso', 'Elegibilidad',
     'Categoría recomendada', 'Categoría líder', 'Mapa de Talento Final', 'Estado proceso', 'Confianza', 'Evidencia principal',
     'Evidencia Effective', 'Evidencia Affective', 'Evidencia Perspective', 'Fortalezas', 'Oportunidades', 'Alertas', 'Próximo paso',
     'Modificado por', 'Fuente', 'Última actualización', 'Versión'],
    records.map(r => [
      r.employeeId ?? '', r.employeeEmail, r.employeeName, r.employeeLastName, r.employeeRole ?? '', r.impactLevel, r.employeeTeam ?? '', r.employeeArea ?? '',
      r.managerEmail ?? r.leaderEmail, r.leaderEmail, r.employeeN1Manager ?? '', r.employeeCountry ?? '', r.employeeStartDate ?? '',
      r.eligibilityStatus === 'onboarding' ? 'Onboarding' : r.eligibilityStatus === 'inactive' ? 'Inactivo' : r.eligibilityStatus === 'license' ? 'Licencia' : 'Elegible',
      CATEGORY_LABELS[r.agentRecommendedCategory] ?? '', CATEGORY_LABELS[r.leaderProposedCategory] ?? '', CATEGORY_LABELS[r.finalCategory] ?? '',
      STATUS_LABELS[r.processStatus] ?? r.processStatus,
      displayConfidencePercent(r) != null ? `${displayConfidencePercent(r)}%` : '',
      r.evidenceSummary ?? '',
      (r.evidenceEffective ?? []).join(' | '), (r.evidenceAffective ?? []).join(' | '), (r.evidencePerspective ?? []).join(' | '),
      (r.strengths ?? []).join(' | '), (r.developmentOpportunities ?? []).join(' | '), (r.calibrationAlerts ?? []).join(' | '),
      r.nextStep ?? '', r.hrbpModifiedBy ?? r.updatedBy, r.processStatus === 'hrbp_modified' ? 'HRBP' : 'Líder',
      r.updatedAt ? r.updatedAt.slice(0, 10) : '', String(r.versionNumber ?? ''),
    ]),
  );
}

export function TalentMapTeamTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user} = useAuth();
  const myEmail = user?.email ?? '';
  const { data: tmAccess } = useTalentMapAccess();
  // Config-driven: ve la base completa quien el governance resuelve como People/HRBP/Admin.
  const isPeopleAdmin = tmAccess?.canViewAll ?? false;

  const [scope, setScope] = useState<'team' | 'org'>('team');
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLeader, setFilterLeader] = useState('');
  const [filterClapper, setFilterClapper] = useState('');
  const [filterClapbase, setFilterClapbase] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterN1, setFilterN1] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);

  const { data: cycle } = useCycleConfig();
  const { data: team, isLoading: loadingTeam } = useTalentMapTeam();
  const orgEnabled = isPeopleAdmin && scope === 'org';
  const { data: orgRecords, isLoading: loadingOrg } = useTalentMapAllForPeople(orgEnabled);
  const logExport = useTalentMapLogExport();
  const syncAll = useTalentMapSyncAll();
  const setLicense = useSetTalentLicense();

  const isLoading = scope === 'org' ? loadingOrg : loadingTeam;

  const rows: TalentTeamRow[] = useMemo(() => {
    if (scope === 'org') {
      return (orgRecords ?? []).map(r => ({
        email: r.employeeEmail,
        employeeId: r.employeeId ?? null,
        name: r.employeeName,
        lastName: r.employeeLastName,
        impactLevel: r.impactLevel,
        role: r.employeeRole ?? null,
        team: r.employeeTeam ?? null,
        area: r.employeeArea ?? null,
        startDate: r.employeeStartDate ?? null,
        country: r.employeeCountry ?? null,
        n1Manager: r.employeeN1Manager ?? null,
        clapbase: r.employeeClapbase ?? null,
        leaderEmail: r.leaderEmail,
        managerEmail: r.managerEmail ?? r.leaderEmail,
        eligibilityStatus: r.eligibilityStatus,
        processStatus: r.processStatus,
        finalCategory: (r.finalCategory ?? null) as TalentCategory | null,
        confidencePercent: displayConfidencePercent(r),
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null,
        hrbpModifiedBy: r.hrbpModifiedBy ?? null,
        hrbpModifiedAt: r.hrbpModifiedAt ?? null,
      }));
    }
    return (team ?? []).map(m => ({
      email: m.email,
      employeeId: m.employeeId ?? null,
      name: m.name,
      lastName: m.lastName,
      impactLevel: m.impactLevel,
      role: m.role ?? null,
      team: m.team ?? null,
      area: m.area ?? null,
      startDate: m.startDate ?? null,
      country: m.country ?? null,
      n1Manager: m.n1Manager ?? null,
      clapbase: m.clapbase ?? null,
      // Scope recursivo: el server devuelve el evaluador real de cada fila
      leaderEmail: m.leaderEmail ?? myEmail,
      leaderName: m.leaderName ?? null,
      managerEmail: m.managerEmail ?? m.leaderEmail ?? myEmail,
      isDirect: m.isDirect ?? true,
      canEvaluate: m.canEvaluate ?? m.isDirect ?? true,
      eligibilityStatus: m.eligibilityStatus,
      processStatus: m.processStatus,
      finalCategory: (m.finalCategory ?? null) as TalentCategory | null,
      confidencePercent: displayConfidencePercent(m),
      updatedAt: m.updatedAt ?? null,
      updatedBy: m.updatedBy ?? null,
      hrbpModifiedBy: m.hrbpModifiedBy ?? null,
      hrbpModifiedAt: m.hrbpModifiedAt ?? null,
    }));
  }, [scope, team, orgRecords, myEmail]);

  const unique = (fn: (r: TalentTeamRow) => string | null | undefined) =>
    Array.from(new Set(rows.map(fn).filter(Boolean) as string[])).sort();
  // Filtro por manager ACTUAL (community map), no por quien evaluó: tras una
  // reorg la evaluación del líder anterior tiene que aparecer bajo el nuevo.
  const uniqueLeaders = useMemo(() => unique(r => r.managerEmail ?? r.leaderEmail), [rows]);
  const uniqueLevels = useMemo(() => unique(r => r.impactLevel), [rows]);
  const uniqueCountries = useMemo(() => unique(r => r.country), [rows]);
  const uniqueN1 = useMemo(() => unique(r => r.n1Manager), [rows]);
  const uniqueAreas = useMemo(() => unique(r => r.area), [rows]);
  const uniqueTeams = useMemo(() => unique(r => r.team), [rows]);
  const uniqueClapbases = useMemo(() => unique(r => r.clapbase), [rows]);
  const uniqueClappers = useMemo(
    () => Array.from(new Map(rows.map(r => [r.email, `${r.name} ${r.lastName}`.trim()])).entries())
      .sort((a, b) => a[1].localeCompare(b[1], 'es')),
    [rows],
  );

  // Un solo filtro de estado, por BUCKET (curveBucket): los 7 estados que pidió
  // Sol el 21/09 (Onboarding, Licencia, Low, Cumple · Sin confirmar, Cumple,
  // Sólida, Top), con el conteo real del scope. Reemplaza a los selects de
  // estado del proceso (Borrador/Pendiente evidencia/Confirmada/Automática) y
  // de elegibilidad: la leyenda de la curva, el chip de cada fila y este filtro
  // dicen exactamente lo mismo.
  const categoryCounts = useMemo(() => countBy(rows, r => curveBucket(r)), [rows]);
  const withCount = (label: string, n: number | undefined) => `${label} (${n ?? 0})`;

  const filtered = useMemo(() => rows.filter(r => {
    if (search) {
      const q = search.toLowerCase();
      if (!(`${r.name} ${r.lastName}`.toLowerCase().includes(q) || r.email.toLowerCase().includes(q))) return false;
    }
    if (filterCategory && curveBucket(r) !== filterCategory) return false;
    if (filterLeader && (r.managerEmail ?? r.leaderEmail) !== filterLeader) return false;
    if (filterClapper && r.email !== filterClapper) return false;
    if (filterClapbase && (r.clapbase ?? '') !== filterClapbase) return false;
    if (filterLevel && r.impactLevel !== filterLevel) return false;
    if (filterCountry && (r.country ?? '') !== filterCountry) return false;
    if (filterN1 && (r.n1Manager ?? '') !== filterN1) return false;
    if (filterArea && (r.area ?? '') !== filterArea) return false;
    if (filterTeam && (r.team ?? '') !== filterTeam) return false;
    return true;
  }), [rows, search, filterCategory, filterLeader, filterClapper, filterClapbase, filterLevel, filterCountry, filterN1, filterArea, filterTeam]);

  // En scope 'org' son ~350 filas montadas de una (el max-h con overflow
  // recorta visualmente pero renderiza todo igual).
  const { pageItems: pageRows, footer: paginationFooter } = usePagination(filtered, 25);

  const hasExtraFilters = !!(filterLeader || filterClapper || filterClapbase || filterLevel || filterCountry || filterN1 || filterArea || filterTeam);
  const clearFilters = () => {
    setSearch(''); setFilterCategory('');
    setFilterLeader(''); setFilterClapper(''); setFilterClapbase('');
    setFilterLevel(''); setFilterCountry(''); setFilterN1(''); setFilterArea(''); setFilterTeam('');
  };

  function handleExport() {
    if (scope === 'org') {
      const exportRecords = (orgRecords ?? []).filter(r => filtered.some(f => f.email === r.employeeEmail && f.leaderEmail === r.leaderEmail));
      exportOrgCSV(exportRecords);
      logExport.mutate({
        type: 'people_admin',
        rowCount: exportRecords.length,
        filters: Object.fromEntries(Object.entries({ search, filterCategory, filterLeader, filterClapper, filterClapbase, filterLevel, filterCountry, filterN1, filterArea, filterTeam }).filter(([, v]) => !!v)),
      });
    } else {
      exportTeamCSV(filtered);
      logExport.mutate({ type: 'leader_team', rowCount: filtered.length });
    }
  }

  const licenseStats = useMemo(() => {
    const count = filtered.filter(isOnLicense).length;
    return { count, pct: filtered.length > 0 ? Math.round((count / filtered.length) * 1000) / 10 : 0 };
  }, [filtered]);

  // Licencias (Sol 18/09, como D-Talk): HRBP/Admin de People sobre toda la
  // base (canEditCategory: platform_admin ve pero no edita); un líder solo
  // sobre la gente que puede evaluar (pm o n1).
  const canEditAll = tmAccess?.canEditCategory ?? false;
  const licenseRows = useMemo(
    () => rows
      .filter(r => canEditAll || (scope === 'team' && r.canEvaluate !== false))
      .map(r => ({
        employeeEmail: r.email,
        employeeName: r.name,
        employeeLastName: r.lastName,
        employeeArea: r.area ?? null,
        employeeCountry: r.country ?? null,
        eligibilityStatus: r.eligibilityStatus,
      })),
    [rows, scope, canEditAll],
  );

  function handleRowClick(row: TalentTeamRow) {
    // Solo quien puede evaluar (pm o n1) entra al chat; el resto de la
    // estructura (scope recursivo) va al histórico en solo-lectura.
    const isMine = scope === 'org' ? row.leaderEmail === myEmail : (row.canEvaluate ?? row.isDirect ?? true);
    const target = isMine ? 'evaluation' : 'history';
    navigate(`/performance/talent-map/${target}/${encodeURIComponent(row.email)}`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
      {/* ─── Center panel ─── */}
      <div className="space-y-4 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('performance.talent_map.team_tab.title')}</h1>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-600 mt-1">
              {t('performance.talent_map.people_period', { period: formatPeriodLabel(cycle) })}
            </p>
          </div>
          {isPeopleAdmin && (
            <div className="flex items-center gap-2">
              <select
                value={scope}
                onChange={e => setScope(e.target.value as 'team' | 'org')}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              >
                <option value="team">{t('performance.talent_map.team_tab.scope_my_team')}</option>
                <option value="org">{t('performance.talent_map.team_tab.scope_org')}</option>
              </select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label={t('common.a11y.more_actions')} className="h-9 w-9">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowConfig(v => !v)}>
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    {t('performance.talent_map.configure_cycle')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => syncAll.mutate()} disabled={syncAll.isPending}>
                    <RefreshCw className={cn('h-4 w-4 mr-2', syncAll.isPending && 'animate-spin')} />
                    {t('performance.talent_map.sync_sheets')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {showConfig && isPeopleAdmin && <CycleConfigPanel />}

        {/* "¿Cómo voy?" del líder: solo sobre SU equipo, no en la vista org */}
        {scope === 'team' && rows.length > 0 && (
          <LeaderProgressCard
            rows={rows}
            onOpen={email => navigate(`/performance/talent-map/evaluation/${encodeURIComponent(email)}`)}
          />
        )}

        {rows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              {t('performance.talent_map.no_direct_reports')}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={t('performance.talent_map.team_tab.search_placeholder')}
                  className="pl-9 h-9 text-sm"
                />
              </div>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              >
                <option value="">{t('performance.talent_map.filter_all_statuses')}</option>
                {CURVE_ORDER.map(k => (
                  <option key={k} value={k}>{withCount(curvePointMeta(k, t as (key: string, fallback?: string) => string).label, categoryCounts.get(k))}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" className="h-9 gap-2" onClick={handleExport} disabled={filtered.length === 0 || logExport.isPending}>
                <Download className="h-4 w-4" />
                {t('performance.talent_map.team_tab.export')}
              </Button>
            </div>

            {/* Filtros (doc 02/09: reemplazan las cards de categorías; impactan
                listado + panel derecho). Los 4 del doc para todos; extras en scope org. */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { value: filterClapper, onChange: setFilterClapper, all: t('performance.talent_map.team_tab.filter_all_clappers', 'Clapper: todos'), opts: uniqueClappers.map(([email, nm]) => [email, nm] as [string, string]) },
                { value: filterLeader, onChange: setFilterLeader, all: t('performance.talent_map.filter_all_managers'), opts: uniqueLeaders.map(e => [e, e.split('@')[0]] as [string, string]) },
                { value: filterLevel, onChange: setFilterLevel, all: t('performance.talent_map.filter_all_levels'), opts: uniqueLevels.map(l => [l, l] as [string, string]) },
                { value: filterClapbase, onChange: setFilterClapbase, all: t('performance.talent_map.team_tab.filter_all_clapbases', 'Clapbase: todas'), opts: uniqueClapbases.map(c => [c, c] as [string, string]) },
                ...(orgEnabled ? [
                  { value: filterArea, onChange: setFilterArea, all: t('performance.talent_map.team_tab.filter_all_areas'), opts: uniqueAreas.map(a => [a, a] as [string, string]) },
                  { value: filterTeam, onChange: setFilterTeam, all: t('performance.talent_map.team_tab.filter_all_teams'), opts: uniqueTeams.map(x => [x, x] as [string, string]) },
                  { value: filterN1, onChange: setFilterN1, all: t('performance.talent_map.filter_all_n1'), opts: uniqueN1.map(m => [m, m.split('@')[0]] as [string, string]) },
                  { value: filterCountry, onChange: setFilterCountry, all: t('performance.talent_map.filter_all_countries'), opts: uniqueCountries.map(c => [c, c] as [string, string]) },
                ] : []),
              ].map((sel, i) => (
                <select
                  key={i}
                  value={sel.value}
                  onChange={e => sel.onChange(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground max-w-[170px]"
                >
                  <option value="">{sel.all}</option>
                  {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              {(hasExtraFilters || search || filterCategory) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 h-8 text-xs text-muted-foreground">
                  <X className="h-3 w-3" />
                  {t('performance.talent_map.clear_filters')}
                </Button>
              )}
            </div>

            {/* Table */}
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="hidden sm:grid grid-cols-[2fr_1.4fr_1fr_1.4fr_0.9fr_auto] gap-3 px-5 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b bg-muted/30">
                  <span>{t('performance.talent_map.team_tab.col_name')}</span>
                  <span>{t('performance.talent_map.role_label')}</span>
                  <span>{t('performance.talent_map.team_tab.col_level')}</span>
                  <span>{t('performance.talent_map.team_tab.col_final_category')}</span>
                  <span>{t('performance.talent_map.team_tab.col_updated')}</span>
                  <span />
                </div>

                {filtered.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-sm text-muted-foreground">{t('performance.talent_map.no_results')}</p>
                  </div>
                ) : (
                  <div className="divide-y max-h-[58vh] overflow-y-auto">
                    {pageRows.map(row => {
                      const isOnboarding = row.eligibilityStatus === 'onboarding';
                      const isLicense = isOnLicense(row);
                      // El chip dice el MISMO estado que la leyenda y el filtro (curveBucket):
                      // lo no confirmado es "Cumple con oportunidades · Sin confirmar", aunque
                      // el líder tenga un borrador con otra categoría (va en el tooltip).
                      const bucket = curveBucket(row);
                      const unconfirmed = bucket === 'cumple_sin_confirmar';
                      const catCfg = isOnboarding
                        ? CATEGORY_CONFIG.onboarding
                        : unconfirmed ? CATEGORY_CONFIG.cumple_con_oportunidades
                        : row.finalCategory ? CATEGORY_CONFIG[row.finalCategory] : null;
                      const draftCategory = unconfirmed && row.finalCategory && row.finalCategory !== 'cumple_con_oportunidades' && CATEGORY_CONFIG[row.finalCategory]
                        ? t(CATEGORY_CONFIG[row.finalCategory].labelKey) : null;
                      const fullName = `${row.name} ${row.lastName}`;
                      return (
                        <button
                          key={`${row.leaderEmail}:${row.email}`}
                          onClick={() => handleRowClick(row)}
                          className="w-full grid grid-cols-1 sm:grid-cols-[2fr_1.4fr_1fr_1.4fr_0.9fr_auto] gap-3 items-center px-5 py-3.5 hover:bg-accent/50 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold shrink-0 text-gray-700"
                              style={{ backgroundColor: getAvatarColor(fullName) }}
                            >
                              {getInitials(row.name, row.lastName)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm truncate">{fullName}</p>
                              {/* Evaluada por otro líder (org, indirecto o reorg): se dice quién. */}
                              {(scope === 'org' || row.isDirect === false || row.leaderEmail !== myEmail) && row.leaderEmail && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {t('performance.talent_map.team_tab.evaluated_by', {
                                    name: row.leaderName || row.leaderEmail.split('@')[0],
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="hidden sm:block text-sm text-muted-foreground truncate">{row.role ?? '—'}</span>
                          <span className="hidden sm:block text-sm text-muted-foreground truncate">{row.impactLevel}</span>
                          <div className="hidden sm:flex items-center gap-1.5">
                            {isLicense ? (
                              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                                {t('performance.talent_map.eligibility_license')}
                              </span>
                            ) : catCfg ? (
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
                                  catCfg.chip,
                                  unconfirmed && 'opacity-70 ring-1 ring-inset ring-current/30',
                                )}
                                title={unconfirmed ? (draftCategory ? t('performance.talent_map.team_tab.draft_hint', { category: draftCategory }) : t('performance.talent_map.team_tab.unconfirmed_hint')) : undefined}
                              >
                                {isOnboarding ? t('performance.talent_map.category_onboarding') : t(catCfg.labelKey)}
                                {unconfirmed && (
                                  <span className="ml-1 font-normal">· {t('performance.talent_map.team_tab.unconfirmed')}</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                            {row.processStatus === 'pending_evidence' && <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                            {row.hrbpModifiedBy && <AlertTriangle className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
                          </div>
                          <span className="hidden sm:block text-sm text-muted-foreground tabular-nums">
                            {row.updatedAt ? format(new Date(row.updatedAt), 'dd/MM/yyyy', { locale: es }) : '—'}
                          </span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
                {filtered.length > 0 && (
                  <div className="px-5 py-2.5 border-t bg-muted/20">
                    {paginationFooter ?? (
                      <p className="text-xs text-muted-foreground">
                        {t('performance.talent_map.team_tab.showing_range', { from: 1, to: filtered.length, total: filtered.length })}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* ─── Right panel ─── */}
      {rows.length > 0 && (
        <div className="lg:sticky lg:top-4 space-y-4">
          {/* Reactivo a los filtros (doc 02/09): distribución, confianza e
              insights se recalculan sobre lo filtrado */}
          <TeamDistributionPanel rows={filtered} />

          {/* Clappers en licencia — la misma card que D-Talk (Sol 18/09), sobre lo filtrado. */}
          <LicenseCard
            count={licenseStats.count}
            pct={licenseStats.pct}
            hint={t('performance.talent_map.team_tab.license_card_hint')}
            manageLabel={t('performance.talent_map.team_tab.manage_licenses')}
            onManage={licenseRows.length > 0 ? () => setShowLicenses(true) : undefined}
          />
        </div>
      )}

      <DTalkLicenseManagerDialog
        open={showLicenses}
        onOpenChange={setShowLicenses}
        rows={licenseRows}
        pending={setLicense.isPending}
        description={t('performance.talent_map.team_tab.license_dialog_hint')}
        onToggleLicense={(employeeEmail, excluded) => setLicense.mutate({ employeeEmail, excluded })}
      />
    </div>
  );
}
